import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/context/LangContext";

/**
 * Reusable newsletter subscribe component, 3 variants:
 *   - inline (default): horizontal form with email + button
 *   - card: stylized box with title + description
 *   - footer: minimal text + button on dark backgrounds
 *
 * Usage:
 *   <NewsletterSubscribe />
 *   <NewsletterSubscribe variant="card" source="home_hero" />
 *   <NewsletterSubscribe variant="footer" source="public_footer" />
 */
export default function NewsletterSubscribe({ variant = "inline", source = "public", className = "" }) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error(t("nl_invalid_email"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/newsletter-subscribe", { email: email.trim().toLowerCase(), source });
      setDone(true);
      toast.success(`${t("nl_success_toast")} ✉️`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className={`inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 ${className}`} data-testid="newsletter-success">
        <CheckCircle2 className="h-4 w-4" /> {t("nl_confirmed")}
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div className={`rounded-3xl bg-gradient-to-br from-wine-50 via-white to-amber-50 p-8 shadow-sm border border-wine-100 ${className}`} data-testid="newsletter-card">
        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-wine-500 text-white"><Mail className="h-6 w-6" /></div>
        <h3 className="mt-4 font-serif text-2xl text-navy">{t("nl_card_title")}</h3>
        <p className="mt-1 text-sm text-neutral-600">{t("nl_card_desc")}</p>
        <form onSubmit={submit} className="mt-5 flex flex-col gap-2 sm:flex-row">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("nl_email_ph")} className="flex-1 rounded-full border border-neutral-200 bg-white px-4 py-2.5 text-sm" data-testid="newsletter-email-card" />
          <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-wine-500 px-6 py-2.5 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-60" data-testid="newsletter-submit-card">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("nl_subscribe")}
          </button>
        </form>
        <p className="mt-2 text-[11px] text-neutral-400">{t("nl_gdpr")}</p>
      </div>
    );
  }

  if (variant === "footer") {
    return (
      <form onSubmit={submit} className={`flex flex-col gap-2 sm:flex-row sm:items-center ${className}`} data-testid="newsletter-footer">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("nl_email_ph")} className="flex-1 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm text-white placeholder-white/50 outline-none focus:border-white/40" data-testid="newsletter-email-footer" />
        <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white px-5 py-2 text-sm font-bold text-wine-700 hover:bg-wine-50 disabled:opacity-60" data-testid="newsletter-submit-footer">
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("nl_subscribe")}
        </button>
      </form>
    );
  }

  // inline default
  return (
    <form onSubmit={submit} className={`flex flex-col gap-2 sm:flex-row sm:items-center ${className}`} data-testid="newsletter-inline">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("nl_email_ph")} className="flex-1 rounded-full border border-neutral-300 bg-white px-4 py-2 text-sm" data-testid="newsletter-email-inline" />
      <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-60" data-testid="newsletter-submit-inline">
        {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Mail className="h-3.5 w-3.5" /> {t("nl_subscribe")}</>}
      </button>
    </form>
  );
}
